import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errMsg } from "@/components/email/EmParts";

const P = "/email-marketing/consent-verify";
const NAMES = { verified: "Verified active consent", review: "Under review", blocked: "Opted out / suppressed", no_evidence: "No evidence" };
const nice = (k) => k.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

function Row({ x }) {
  const v = x.consent_verification || {};
  return (
    <div className="rounded-lg border border-slate-200 p-3 text-sm" data-testid={`cv-row-${x.id}`}>
      <div className="flex flex-wrap gap-2 justify-between"><b>{`${x.first_name || ""} ${x.last_name || ""}`.trim() || "—"} <span className="font-normal text-slate-500">{x.email}</span></b>
        <span className="text-xs text-slate-500">Now: {x.marketing_permission} · {v.reason}</span></div>
      {(v.evidence || []).map((e, i) => <p key={`e${i}`} className="text-xs text-emerald-700">✓ {e.source} {e.at} {e.detail}</p>)}
      {(v.weak_evidence || []).map((e, i) => <p key={`w${i}`} className="text-xs text-amber-700">? {e.source} {e.at}</p>)}
      {(v.opt_outs || []).map((e, i) => <p key={`o${i}`} className="text-xs text-rose-700">✕ {e.source} {e.at}</p>)}
    </div>
  );
}

function Records() {
  const [st, setSt] = useState("verified");
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`${P}/list`, { params: { status: st, page } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [st, page]);
  return (
    <div className="mt-4 space-y-2">
      <div className="flex flex-wrap gap-2">{Object.entries(NAMES).map(([k, n]) => (
        <Button key={k} size="sm" variant={st === k ? "default" : "outline"} onClick={() => { setSt(k); setPage(1); }} data-testid={`cv-filter-${k}`}>{n}</Button>))}</div>
      <p className="text-xs text-slate-500" data-testid="cv-list-total">{d ? `${d.total} records` : "Loading…"}</p>
      {d?.items.map((x) => <Row key={x.id} x={x} />)}
      {d && d.total > 25 && <div className="flex gap-2"><Button size="sm" variant="outline" disabled={page < 2} onClick={() => setPage(page - 1)} data-testid="cv-prev">Previous</Button>
        <Button size="sm" variant="outline" disabled={page * 25 >= d.total} onClick={() => setPage(page + 1)} data-testid="cv-next">Next</Button></div>}
    </div>
  );
}

function Apply({ run, reload }) {
  const [txt, setTxt] = useState("");
  const go = async () => { try { await api.post(`${P}/apply`, { run_id: run.id, confirm: txt }); toast.success("Applying verified consent"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="cv-apply">
      <p>Owner approval: marks the verified records as marketing-eligible and holds currently-granted records without enough evidence. Nothing is sent; sending and automation switches stay as they are.</p>
      <div className="flex flex-wrap gap-2 mt-2"><Input value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="Type APPLY VERIFIED CONSENT" className="max-w-xs bg-white" data-testid="cv-apply-input" />
        <Button size="sm" onClick={go} disabled={txt !== "APPLY VERIFIED CONSENT"} data-testid="cv-apply-btn">Apply</Button></div>
    </div>
  );
}

export function ConsentVerification() {
  const [run, setRun] = useState(null);
  const load = useCallback(() => api.get(`${P}/latest`).then((r) => setRun(r.data)).catch(() => setRun({ status: "none" })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!["running", "applying"].includes(run?.status)) { return undefined; }
    const t = setTimeout(load, 3000);
    return () => clearTimeout(t);
  }, [run, load]);
  const start = async () => { try { await api.post(`${P}/run`); toast.success("Verification started"); load(); } catch (e) { toast.error(errMsg(e)); } };
  const c = run?.counts || {};
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="cv-section">
      <div className="flex flex-wrap items-center gap-2"><div className="mr-auto max-w-3xl"><h2 className="text-base font-bold text-slate-900">Verify existing marketing permissions</h2>
        <p className="text-sm text-slate-500">Checks every record against checkout opt-ins and historic Mailcoach / Mailchimp signups, keeping source, date, wording and any later opt-out. Running it records evidence only.</p></div>
        <Button size="sm" variant="outline" onClick={start} disabled={["running", "applying"].includes(run?.status)} data-testid="cv-run">{run?.status === "running" ? "Checking…" : "Run verification"}</Button></div>
      {run && run.status !== "none" && <p className="text-xs text-slate-500 mt-2" data-testid="cv-status">Status: {run.status} · started {String(run.started_at || "").slice(0, 16)}{run.applied ? ` · applied ${JSON.stringify(run.applied)}` : ""}</p>}
      {run?.counts && <>
        <div className="grid gap-2 sm:grid-cols-4 mt-3">{Object.entries(NAMES).map(([k, n]) => (
          <div key={k} className="rounded-lg bg-slate-50 px-3 py-2" data-testid={`cv-count-${k}`}><p className="text-xs text-slate-500">{n}</p><p className="text-xl font-bold">{c[k] || 0}</p></div>))}</div>
        <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3 mt-3">{Object.entries(c).filter(([k]) => k.includes(":")).sort().map(([k, v]) => (
          <div key={k} className="rounded-lg bg-slate-50 px-3 py-1.5 flex justify-between text-sm" data-testid={`cv-break-${k}`}><span className="text-slate-600">{nice(k.replace(":", " → "))}</span><b>{v}</b></div>))}</div>
        {run.status === "done" && <Apply run={run} reload={load} />}
        <Records /></>}
    </section>
  );
}
